(() => {
  const selectors = {
    root: '[data-bundle-components-ui]',
    component: '[data-bundle-component]',
    select: '[data-bundle-variant-select]',
    input: '[data-bundle-variant-input]',
    addButton: '[data-bundle-add-to-cart]',
    quantity: '[data-bundle-quantity]',
    minus: '[data-bundle-quantity-minus]',
    plus: '[data-bundle-quantity-plus]',
    message: '[data-bundle-message]',
    spinner: '[data-bundle-spinner]',
    buttonText: '[data-bundle-button-text]',
  };

  class BundleComponentsUI {
    constructor(element) {
      this.element = element;
      this.addButton = element.querySelector(selectors.addButton);
      this.quantityInput = element.querySelector(selectors.quantity);
      this.minusButton = element.querySelector(selectors.minus);
      this.plusButton = element.querySelector(selectors.plus);
      this.message = element.querySelector(selectors.message);
      this.spinner = element.querySelector(selectors.spinner);
      this.buttonText = element.querySelector(selectors.buttonText);
      this.defaultButtonText = this.buttonText?.textContent?.trim() || 'Add Bundle To Cart';
      this.successResetTimer = null;

      this.bindEvents();
    }

    bindEvents() {
      this.addButton?.addEventListener('click', this.handleAddToCart.bind(this));
      this.minusButton?.addEventListener('click', () => this.changeQuantity(-1));
      this.plusButton?.addEventListener('click', () => this.changeQuantity(1));

      this.quantityInput?.addEventListener('change', () => {
        this.quantityInput.value = this.getQuantity();
      });
    }

    changeQuantity(delta) {
      if (!this.quantityInput) return;
      this.quantityInput.value = Math.max(1, this.getQuantity() + delta);
    }

    getQuantity() {
      return Math.max(1, parseInt(this.quantityInput?.value, 10) || 1);
    }

    getSelectedComponents() {
      return Array.from(this.element.querySelectorAll(selectors.component)).map((component) => {
        const selector = component.querySelector(selectors.select);
        const input = component.querySelector(selectors.input);
        const selectedOption = selector?.options?.[selector.selectedIndex];
        const variantId = selector?.value || input?.value || '';
        const variantTitle = selectedOption?.dataset.variantTitle || input?.dataset.variantTitle || '';

        return {
          variantId,
          variantTitle,
          productId: component.dataset.componentProductId || '',
          title: component.dataset.componentTitle || '',
          index: component.dataset.componentIndex || '',
        };
      });
    }

    validateComponents(components) {
      if (!components.length) return 'No included products are configured for this bundle yet.';
      if (components.some((component) => !component.variantId)) {
        return 'Please select one available size for every included product.';
      }
      return '';
    }

    buildCartPayload() {
      const quantity = this.getQuantity();
      const components = this.getSelectedComponents();
      const validationError = this.validateComponents(components);
      if (validationError) throw new Error(validationError);

      const bundleId = `bundle-${this.element.dataset.parentProductId || 'product'}-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 8)}`;
      const componentCount = this.element.dataset.componentCount || String(components.length);

      return {
        items: components.map((component) => ({
          id: Number(component.variantId),
          quantity,
          properties: {
            _bundle_id: bundleId,
            _bundle_role: 'component',
            _bundle_parent_product_id: this.element.dataset.parentProductId || '',
            _bundle_parent_variant_id: this.element.dataset.parentVariantId || '',
            _bundle_parent_title: this.element.dataset.parentTitle || '',
            _bundle_parent_handle: this.element.dataset.parentHandle || '',
            _bundle_parent_image: this.element.dataset.parentImage || '',
            _bundle_parent_price: this.element.dataset.parentPrice || '',
            _bundle_component_count: componentCount,
            _bundle_component_index: component.index,
            _bundle_component_product_id: component.productId,
            _bundle_component_title: component.title,
            _bundle_component_variant_title: component.variantTitle,
          },
        })),
      };
    }

    getSectionsToRender() {
      const sections = [];

      try {
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:grouped-sections', {
            bubbles: true,
            detail: { sections },
          })
        );
      } catch (error) {
        console.warn('Unable to collect cart sections.', error);
      }

      return sections;
    }

    async handleAddToCart() {
      if (!this.addButton || this.addButton.disabled) return;

      try {
        this.setLoading(true);
        this.showMessage('', '');

        const payload = this.buildCartPayload();
        const sections = this.getSectionsToRender();
        payload.sections = sections;
        payload.sections_url = window.location.pathname;

        const response = await fetch(this.getCartAddUrl(), {
          ...this.getFetchConfig(),
          body: JSON.stringify(payload),
        });

        const parsedState = await response.json();

        if (!response.ok || parsedState.status) {
          throw new Error(parsedState.description || parsedState.message || 'Unable to add this bundle to cart.');
        }

        await this.refreshCart(parsedState);
        this.setSuccess();
      } catch (error) {
        this.setLoading(false);
        this.showMessage(error.message || 'Unable to add this bundle to cart.', 'error');
      }
    }

    async refreshCart(parsedState) {
    const cartDrawer = document.querySelector('cart-drawer');
    const rootUrl = window.FoxTheme?.routes?.root_url || window.Shopify?.routes?.root || '/';
    const cartJsonUrl = `${rootUrl}cart.js`;
    const hasSections = parsedState.sections && Object.keys(parsedState.sections).length > 0;

    document.dispatchEvent(
        new CustomEvent('product-ajax:added', {
        detail: { product: parsedState },
        })
    );

    if (window.FoxTheme?.pubsub?.publish && window.FoxTheme?.pubsub?.PUB_SUB_EVENTS?.cartUpdate && hasSections) {
        const cartResponse = await fetch(cartJsonUrl, {
        method: 'GET',
        headers: {
            Accept: 'application/json',
        },
        });

        const cartText = await cartResponse.text();
        let cartJson;

        try {
        cartJson = JSON.parse(cartText);
        } catch (error) {
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }));
        return;
        }

        cartJson.sections = parsedState.sections || {};

        window.FoxTheme.pubsub.publish(window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
        cart: cartJson,
        target: this.addButton,
        });

        if (cartDrawer && typeof cartDrawer.show === 'function') {
        cartDrawer.show(this.addButton);
        }

        return;
    }

    document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }));

    if (cartDrawer && typeof cartDrawer.show === 'function') {
        cartDrawer.show(this.addButton);
    }
    }

    getCartAddUrl() {
      return window.FoxTheme?.routes?.cart_add_url || `${window.Shopify?.routes?.root || '/'}cart/add.js`;
    }

    getFetchConfig() {
      if (window.FoxTheme?.utils?.fetchConfig) {
        return window.FoxTheme.utils.fetchConfig('javascript');
      }

      return {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      };
    }

    setLoading(isLoading) {
      if (!this.addButton) return;

      if (this.successResetTimer) {
        window.clearTimeout(this.successResetTimer);
        this.successResetTimer = null;
      }

      this.addButton.classList.toggle('is-loading', isLoading);
      this.addButton.classList.remove('is-success');
      this.addButton.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      this.addButton.setAttribute('aria-disabled', isLoading ? 'true' : 'false');
      this.addButton.disabled = isLoading;
      if (this.spinner) this.spinner.hidden = !isLoading;

      if (this.buttonText) {
        this.buttonText.textContent = isLoading ? 'Adding bundle to cart...' : this.defaultButtonText;
      }
    }

    setSuccess() {
      if (!this.addButton) return;

      this.addButton.classList.remove('is-loading');
      this.addButton.classList.add('is-success');
      this.addButton.setAttribute('aria-busy', 'false');
      this.addButton.setAttribute('aria-disabled', 'true');
      this.addButton.disabled = true;
      if (this.spinner) this.spinner.hidden = true;

      if (this.buttonText) {
        this.buttonText.textContent = 'Bundle added';
      }

      this.successResetTimer = window.setTimeout(() => {
        this.addButton.classList.remove('is-success');
        this.addButton.removeAttribute('aria-disabled');
        this.addButton.disabled = false;

        if (this.buttonText) {
          this.buttonText.textContent = this.defaultButtonText;
        }

        this.successResetTimer = null;
      }, 2000);
    }

    showMessage(message, type) {
      if (!this.message) return;

      this.message.textContent = message;
      this.message.classList.remove('is-error');
      this.message.hidden = !message;

      if (type === 'error') {
        this.message.classList.add('is-error');
      }
    }
  }

  const initBundleComponentsUI = (scope = document) => {
    scope.querySelectorAll(selectors.root).forEach((element) => {
      if (element.dataset.bundleComponentsUiInitialized === 'true') return;
      element.dataset.bundleComponentsUiInitialized = 'true';
      new BundleComponentsUI(element);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initBundleComponentsUI());
  } else {
    initBundleComponentsUI();
  }

  document.addEventListener('shopify:section:load', (event) => initBundleComponentsUI(event.target));
})();
